import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import RecurrenceModal from 'components/RecurringPattern/RecurrenceModal';
import { RecurringPatternSummaryStyle, RecurringSummary } from 'components/RecurringPattern/RecurringSummary';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium } from 'globalStyles';
import RecurringPattern from 'models/recurringPattern';
import Task, { TaskTypes } from 'models/tasks/task';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canShowTaskRecurrence, getTaskForRecurrenceDialog, isLegacyNormalSeries } from './taskDetailRules';
import { iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailRecurrenceProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  onUpdate: (newTask: Task) => void;
}

interface ITaskDetailRecurrenceModalProps {
  task: Task;
  onUpdate: (newTask: Task) => void;
  isOpen: boolean;
  toggleModal: () => void;
}

// The recurrence dialog with the way its result is stored on the task
export const TaskDetailRecurrenceModal = (props: ITaskDetailRecurrenceModalProps) => {
  const recurringChangeHandler = (recurring: RecurringPattern) => {
    const copyTask = props.task.clone();
    copyTask.recurringPattern = recurring;
    if (recurring.isActive) {
      copyTask.setNewStartDate(recurring.startDate);
    }
    props.onUpdate(copyTask);
  };

  return (
    <RecurrenceModal
      isOpen={props.isOpen}
      toggleModal={props.toggleModal}
      recurring={props.task.recurringPattern}
      changeRecurring={recurringChangeHandler}
      initToAuditYear={false}
      //only annual plan (Monitoring) series support it; on other series a pattern change recreates the instances
      showPreventBeforeStart={props.task.taskType === TaskTypes.Monitoring}
    />
  );
};

export const TaskDetailRecurrence = (props: ITaskDetailRecurrenceProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation', 'dateTimeComponent', 'control']);
  const [recurringModalOpen, setRecurringModalOpen] = useState<boolean>(false);
  // an existing legacy series shows its pattern read-only, because the server refuses a changed one
  const isReadonly =
    isLegacyNormalSeries(props.orgTask) ||
    !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Recurrence, appContext);

  const toggleRecurringPattern = () => {
    setRecurringModalOpen(!recurringModalOpen);
  };

  //
  // Main render
  //
  if (!canShowTaskRecurrence(props.orgTask)) {
    return null;
  }

  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="start"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace } } : undefined}
    >
      <IPStack.Item>
        {!appContext.isMobileView && <IPFontIcon iconName="RepeatAll" style={iconFontSize} />}
      </IPStack.Item>
      {isReadonly && (
        <IPStack.Item grow>
          <IPLabel>{t('task:Repeat.Title')}</IPLabel>
          {props.task.recurringPattern.isActive && (
            <RecurringSummary
              pattern={props.task.recurringPattern}
              summaryStyle={RecurringPatternSummaryStyle.text}
              label={t('task:Repeat.Title')}
            />
          )}
          {!props.task.recurringPattern.isActive && <IPText>{'-'}</IPText>}
        </IPStack.Item>
      )}
      {!isReadonly && (
        <IPStack.Item grow>
          <RecurringSummary
            pattern={props.task.recurringPattern}
            summaryStyle={RecurringPatternSummaryStyle.edit}
            label={t('task:Repeat.Title')}
            placeholder={t('task:Repeat.Placeholder')}
            onClick={() => {
              if (!isReadonly) {
                props.onUpdate(getTaskForRecurrenceDialog(props.task));
                toggleRecurringPattern();
              }
            }}
          />
          <TaskDetailRecurrenceModal
            task={props.task}
            onUpdate={props.onUpdate}
            isOpen={recurringModalOpen}
            toggleModal={toggleRecurringPattern}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};
